import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';

const SalesTargetDetailsView = ({ open, onClose, row, cropList, varietyList }) => {
  if (!row) return null;

  const details = Array.isArray(row.salesTargetDetails) ? row.salesTargetDetails : [];

  const getCropName = (cropId) => {
    if (!cropId) return '-';
    const crop = cropList.find((c) => c.cropId === cropId || c.id === cropId);
    return crop?.name || crop?.cropName || `Crop ${cropId}`;
  };

  const getVarietyName = (varietyId) => {
    if (!varietyId) return '-';
    const variety = varietyList.find((v) => v.varietyId === varietyId || v.id === varietyId);
    return variety?.name || variety?.varietyName || `Variety ${varietyId}`;
  };

  const fmt = (v) => (v != null && v !== '' ? String(Number(v)) : '-');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 22 }}>
        Sales Target Details
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Season</Typography>
            <Typography variant="body2" fontWeight={600}>{row.season || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Area Type</Typography>
            <Typography variant="body2" fontWeight={600}>{row.areaType || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Area Name</Typography>
            <Typography variant="body2" fontWeight={600}>{row.areaName || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Sales Target</Typography>
            <Typography variant="body2" fontWeight={600}>{row.salesTarget || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Avg Qty / Acre</Typography>
            <Typography variant="body2" fontWeight={600}>{row.avg_qty_per_acre ? fmt(row.avg_qty_per_acre) : '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Avg Farmer Purch Qty</Typography>
            <Typography variant="body2" fontWeight={600}>{row.avg_farmer_purch_qty ? fmt(row.avg_farmer_purch_qty) : '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Farmer Target</Typography>
            <Typography variant="body2" fontWeight={600}>{row.farmerTarget || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Target Done</Typography>
            <Typography variant="body2" fontWeight={600}>{row.targetDone || '-'}</Typography>
          </Box>
        </Box>

        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
          Crop / Variety Breakdown
        </Typography>

        {details.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid #E5E7EB', borderRadius: 2 }}>
            <Typography color="text.secondary">No crop details available.</Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5 }}>
              {details.map((d, idx) => (
                <Paper key={d.id || idx} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
                  <Typography variant="caption" color="text.secondary">#</Typography>
                  <Typography variant="body2" fontWeight={600} mb={1}>{idx + 1}</Typography>

                  <Typography variant="caption" color="text.secondary">Crop</Typography>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    <Chip label={getCropName(d.crop_id)} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 600, fontSize: 12 }} />
                  </Typography>

                  <Typography variant="caption" color="text.secondary">Variety</Typography>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    <Chip label={getVarietyName(d.variety_id)} size="small" sx={{ bgcolor: '#F0FDF4', color: '#166534', fontWeight: 600, fontSize: 12 }} />
                  </Typography>

                  <Typography variant="caption" color="text.secondary">PY Sales Qty</Typography>
                  <Typography variant="body2" mb={1}>{fmt(d.py_sales_qty)}</Typography>

                  <Typography variant="caption" color="text.secondary">Sales Target</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmt(d.sales_target)}</Typography>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={{ display: { xs: 'none', sm: 'block' }, border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#F3F4F6' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Crop</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Variety</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">PY Sales Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Sales Target</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((d, idx) => (
                    <TableRow key={d.id || idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Chip label={getCropName(d.crop_id)} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 600, fontSize: 12 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={getVarietyName(d.variety_id)} size="small" sx={{ bgcolor: '#F0FDF4', color: '#166534', fontWeight: 600, fontSize: 12 }} />
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmt(d.py_sales_qty)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(d.sales_target)}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SalesTargetDetailsView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
  cropList: PropTypes.array,
  varietyList: PropTypes.array,
};

SalesTargetDetailsView.defaultProps = {
  cropList: [],
  varietyList: [],
};

export default SalesTargetDetailsView;
